// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import { EmptyState } from '@/components/empty-state';
import { TablePaginationFooter } from '@/components/table-pagination-footer';
import { ArrowOutwardRounded } from '@/constants/hardfin-standard-icons';
import { Sort } from '@/constants/sort';
import { useTableSort } from '@/hooks/use-table-sort';
import { LocationFilter } from '@/location/api/get-locations';
import { SiteTableRow } from '@/location/components/site-table-row';
import {
  LocationSortProperty,
  useLocations,
} from '@/location/hooks/use-locations';
import {
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Typography,
} from '@mui/material';
import { useCallback, useEffect, useMemo, useState } from 'react';

interface Props {
  /**
   * The current sort state of the table.
   */
  sort: Sort<LocationSortProperty>;

  /**
   * A filter to apply to the locations requested from the remote API.
   */
  filter?: LocationFilter;
  /**
   * A callback fired when a sort header is clicked.
   */
  onSort: (property: LocationSortProperty) => void;

  /**
   * A callback fired when the user navigates to a different page.
   */
  onPageChange: (page: number) => void;

  /**
   * URL for the CSV export endpoint. When provided, a download link is shown
   * in the table footer.
   */
  exportURL?: URL;
}

/**
 * A table component that renders the name, address, and asset count for each
 * site in a list of sites.
 */
export function SiteTable(props: Props) {
  const { locations, resultCount } = useLocations({ filter: props.filter });
  const [expandedSites, setExpandedSites] = useState<Set<string>>(
    () => new Set()
  );

  const pageNumber = props.filter?.page ?? 1;
  const pageSize = props.filter?.pageSize ?? 50;

  const onPageChange = props.onPageChange;

  const { getSortDirection, getLabelDirection, isLabelActive } = useTableSort(
    props.sort
  );

  const createSortHandler = (column: LocationSortProperty) => () => {
    props.onSort(column);
  };

  const handleToggleExpand = useCallback((siteId: string) => {
    setExpandedSites((prev) => {
      const next = new Set(prev);
      if (next.has(siteId)) {
        next.delete(siteId);
      } else {
        next.add(siteId);
      }
      return next;
    });
  }, []);

  // Reset to page 1 when the current page returns no results (page overrun)
  useEffect(() => {
    if (locations.length === 0 && pageNumber > 1) {
      onPageChange(1);
    }
  }, [locations.length, pageNumber, onPageChange]);

  const hasIntegration = locations.some(
    (location) => location.integrations.length > 0
  );

  const hasAnyZones = locations.some((location) =>
    location.zones.some((zone) => !zone.isArchived || zone.assetCount > 0)
  );

  // Auto-expand sites whose zones match the current search query
  const autoExpandedSites = useMemo(() => {
    const query = props.filter?.query?.toLowerCase();
    if (!query) return new Set<string>();

    return new Set(
      locations
        .filter((site) =>
          site.zones.some((zone) => zone.name.toLowerCase().includes(query))
        )
        .map((site) => site.id)
    );
  }, [locations, props.filter?.query]);

  if (locations.length === 0) {
    return (
      <EmptyState title="No sites found">
        <Stack direction="row" alignItems="center" spacing={1}>
          <Typography color="text.secondary">
            To start tracking locations in Hardfin, add a new location
          </Typography>
          <ArrowOutwardRounded fontSize="small" color="action" />
        </Stack>
      </EmptyState>
    );
  }

  return (
    <>
      <TableContainer component={Paper} variant="outlined">
        <Table>
          <TableHead>
            <TableRow>
              {hasAnyZones && (
                <TableCell
                  sx={{
                    width: '3rem',
                    px: 1,
                  }}
                />
              )}
              {hasIntegration && (
                <TableCell
                  sx={{
                    width: '3rem',
                    px: 2,
                    whiteSpace: 'nowrap',
                  }}
                />
              )}
              <TableCell sortDirection={getSortDirection('name')}>
                <TableSortLabel
                  active={isLabelActive('name')}
                  direction={getLabelDirection('name')}
                  onClick={createSortHandler('name')}
                >
                  Name
                </TableSortLabel>
              </TableCell>
              <TableCell
                sortDirection={getSortDirection('company')}
                sx={{ display: { xs: 'none', sm: 'table-cell' } }}
              >
                <TableSortLabel
                  active={isLabelActive('company')}
                  direction={getLabelDirection('company')}
                  onClick={createSortHandler('company')}
                >
                  Company
                </TableSortLabel>
              </TableCell>
              <TableCell>Address</TableCell>
              <TableCell
                sortDirection={getSortDirection('assetCount')}
                sx={{ display: { xs: 'none', sm: 'table-cell' } }}
              >
                <TableSortLabel
                  active={isLabelActive('assetCount')}
                  direction={getLabelDirection('assetCount')}
                  onClick={createSortHandler('assetCount')}
                >
                  Assets
                </TableSortLabel>
              </TableCell>
              <TableCell />
            </TableRow>
          </TableHead>
          <TableBody>
            {locations.map((site) => (
              <SiteTableRow
                key={site.id}
                site={site}
                hasIntegration={hasIntegration}
                hasAnyZones={hasAnyZones}
                isExpanded={
                  expandedSites.has(site.id) || autoExpandedSites.has(site.id)
                }
                onToggleExpand={() => handleToggleExpand(site.id)}
              />
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      <TablePaginationFooter
        pagination={{
          page: pageNumber,
          pageSize,
          onPageChange,
        }}
        totalCount={resultCount}
        entityOptions={{ singular: 'location', includeCount: false }}
        downloadURL={props.exportURL}
        downloadLabel="Export location data"
      />
    </>
  );
}
